import { LayoutElement, BoundingBox, Position } from '../types';
import { GRID_SIZE } from '../constants';

/**
 * Snap a value to the grid
 */
export const snapToGrid = (value: number, gridSize: number = GRID_SIZE): number => {
  return Math.round(value / gridSize) * gridSize;
};

/**
 * Snap a position to the grid
 */
export const snapPositionToGrid = (position: Position, gridSize: number = GRID_SIZE): Position => {
  return {
    x: snapToGrid(position.x, gridSize),
    y: snapToGrid(position.y, gridSize),
  };
};

/**
 * Calculate bounding box for an element
 */
export const getBoundingBox = (element: LayoutElement): BoundingBox => {
  return {
    x: element.position.x,
    y: element.position.y,
    width: element.dimensions.width,
    height: element.dimensions.height,
    rotation: element.rotation,
  };
};

/**
 * Check if two bounding boxes intersect
 */
export const boxesIntersect = (box1: BoundingBox, box2: BoundingBox): boolean => {
  // Simple AABB collision detection (doesn't account for rotation)
  return (
    box1.x < box2.x + box2.width &&
    box1.x + box1.width > box2.x &&
    box1.y < box2.y + box2.height &&
    box1.y + box1.height > box2.y
  );
};

/**
 * Check if a point is inside a bounding box
 */
export const pointInBox = (point: Position, box: BoundingBox): boolean => {
  return (
    point.x >= box.x &&
    point.x <= box.x + box.width &&
    point.y >= box.y &&
    point.y <= box.y + box.height
  );
};

/**
 * Calculate distance between two points
 */
export const distance = (p1: Position, p2: Position): number => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Clamp a value between min and max
 */
export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Convert degrees to radians
 */
export const degreesToRadians = (degrees: number): number => {
  return (degrees * Math.PI) / 180;
};

/**
 * Convert radians to degrees
 */
export const radiansToDegrees = (radians: number): number => {
  return (radians * 180) / Math.PI;
};

/**
 * Get the center point of an element
 */
export const getElementCenter = (element: LayoutElement): Position => {
  return {
    x: element.position.x + element.dimensions.width / 2,
    y: element.position.y + element.dimensions.height / 2,
  };
};

/**
 * Calculate new position after rotation around a pivot point
 */
export const rotatePoint = (
  point: Position,
  pivot: Position,
  angleDegrees: number
): Position => {
  const angleRad = degreesToRadians(angleDegrees);
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;

  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  };
};

/**
 * Check if elements are aligned (within threshold)
 */
export const areAligned = (
  pos1: number,
  pos2: number,
  threshold: number = 5
): boolean => {
  return Math.abs(pos1 - pos2) < threshold;
};

/**
 * Get alignment suggestions for an element relative to others
 */
export const getAlignmentSuggestions = (
  element: LayoutElement,
  otherElements: LayoutElement[],
  threshold: number = 5
): {
  horizontal: number[];
  vertical: number[];
} => {
  const suggestions = {
    horizontal: [] as number[],
    vertical: [] as number[],
  };

  const center = getElementCenter(element);

  otherElements.forEach((other) => {
    if (other.id === element.id) return;

    const otherCenter = getElementCenter(other);
    const otherBox = getBoundingBox(other);

    // Check horizontal alignments
    if (areAligned(element.position.x, other.position.x, threshold)) {
      suggestions.vertical.push(other.position.x);
    }
    if (areAligned(center.x, otherCenter.x, threshold)) {
      suggestions.vertical.push(otherCenter.x);
    }
    if (
      areAligned(
        element.position.x + element.dimensions.width,
        otherBox.x + otherBox.width,
        threshold
      )
    ) {
      suggestions.vertical.push(otherBox.x + otherBox.width);
    }

    // Check vertical alignments
    if (areAligned(element.position.y, other.position.y, threshold)) {
      suggestions.horizontal.push(other.position.y);
    }
    if (areAligned(center.y, otherCenter.y, threshold)) {
      suggestions.horizontal.push(otherCenter.y);
    }
    if (
      areAligned(
        element.position.y + element.dimensions.height,
        otherBox.y + otherBox.height,
        threshold
      )
    ) {
      suggestions.horizontal.push(otherBox.y + otherBox.height);
    }
  });

  // Remove duplicates
  suggestions.horizontal = [...new Set(suggestions.horizontal)];
  suggestions.vertical = [...new Set(suggestions.vertical)];

  return suggestions;
};

/**
 * Deep clone an object
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Generate a unique ID
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};
